/* Copyright 2024 City Trax Limited. All rights reserved. */
import { useCallback, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { post, ApiError } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
    Container,
    Header,
    Form,
    SpaceBetween,
    Button,
    FormField,
    FileUpload,
    ProgressBar,
    Spinner
} from '@cloudscape-design/components';

import PromptTypeFormField from './PromptTypeFormField';


function TextTunerForm() {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorSubmittingMessage, setErrorSubmittingMessage] = useState('');
    const [errorPromptMessage, setErrorPromptMessage] = useState('');
    const [audioFile, setAudioFile] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [promptType, setPromptType] = useState(null);

    const validateAudioFile = (file) => {
        console.log(file)
        const fileSizeLimit = 2000000000
        if (!file) {
            return 'Please select an audio file'
        } else if (!["application/vnd.openxmlformats-officedocument.presentationml.presentation", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file[0].type)) {
            return 'File type not supported';
        } else if (file[0].size > fileSizeLimit) {
            return 'File size too large, please use a smaller file'
        } else {
            return undefined
        }
    }

    // TODO handle large file uploads in multipart
    // TODO handle file uploads from google drive, requires a google app account
    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;

        const error = validateAudioFile(audioFile)
        if (error !== undefined) {
            setErrorSubmittingMessage(error);
        } else if (promptType == null) {
            setErrorPromptMessage('No prompt selected.')
        } else {

            setIsSubmitting(true);
            setErrorSubmittingMessage('');
            try {
                const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
                if (authToken) {
                    const restOperation = post({
                        apiName: 'textTuner',
                        path: '/file',
                        options: {
                            queryParams: {
                                file: audioFile[0].name
                            },
                            headers: {
                                Authorization: authToken
                            },
                            body: {
                                promptType: promptType,
                            },
                        },

                    });
                    const { body } = await restOperation.response;
                    const json = await body.json();
                    const pre_signed_url = (json)?.pre_signed_url
                    await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.upload.addEventListener('progress', e => {
                            console.log(Math.round((e.loaded / e.total) * 100))
                            setUploadProgress(Math.round((e.loaded / e.total) * 100))
                        });
                        xhr.addEventListener('load', () => resolve({ status: xhr.status, body: xhr.responseText }));
                        xhr.addEventListener('error', () => reject(new Error('File upload failed')));
                        xhr.addEventListener('abort', () => reject(new Error('File upload aborted')));
                        xhr.open('PUT', pre_signed_url, true);
                        xhr.send(audioFile[0]);
                    });
                    setIsSubmitting(false);
                    navigate('/text-tuner/', {
                        state: { submitted: true }
                    });
                }

            } catch (error) {
                if (error instanceof ApiError) {
                    if (error.response) {
                        const {
                            statusCode,
                            body
                        } = error.response;
                        console.error(`Received ${statusCode} error response with payload: ${body}`);
                        setErrorSubmittingMessage(body);
                    }
                    // Handle API errors not caused by HTTP response.
                }
                console.error(error)
                setIsSubmitting(false);
                setErrorSubmittingMessage("Error submitting file");
            }
        }

    }, [isSubmitting, audioFile, promptType, navigate]);


    return (
        <Container
            header={
                <Header variant="h2">
                    New
                </Header>
            }
        >
            <form onSubmit={e => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" variant="link" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={isSubmitting | (audioFile.length === 0) | (promptType == null)}
                            >{isSubmitting ? <Spinner /> : "Submit"}</Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="l">
                        <FormField
                        >
                            <FileUpload
                                onChange={({ detail }) => {
                                    setErrorSubmittingMessage('')
                                    setAudioFile(detail.value)
                                }}
                                value={audioFile}
                                i18nStrings={{
                                    uploadButtonText: e =>
                                        e ? "Choose files" : "Choose file",
                                    dropzoneText: e =>
                                        e
                                            ? "Drop files to upload"
                                            : "Drop file to upload",
                                    removeFileAriaLabel: e =>
                                        `Remove file ${e + 1}`,
                                    limitShowFewer: "Show fewer files",
                                    limitShowMore: "Show more files",
                                    errorIconAriaLabel: "Error"
                                }}
                                showFileLastModified
                                showFileSize
                                showFileThumbnail
                                tokenLimit={3}
                                accept=".pptx,.docx"
                                constraintText="Accepted Formats: .pptx, .docx"
                                fileErrors={[errorSubmittingMessage]}
                            />
                        </FormField>
                        <PromptTypeFormField promptType={promptType} setPromptType={setPromptType} errorPromptMessage={errorPromptMessage} newTranscription={true} />



                    </SpaceBetween>
                </Form>
            </form>
            {isSubmitting ? <ProgressBar
                value={uploadProgress}
                additionalInfo="Uploading File"
            /> : null}

        </Container >


    )



}

export default TextTunerForm;